import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  PhoneIcon,
  ClockIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [humanChallenge, setHumanChallenge] = useState<{ question: string; token: string } | null>(null);
  const [humanAnswer, setHumanAnswer] = useState('');
  const [website, setWebsite] = useState('');

  const loadHumanChallenge = useCallback(async () => {
    setHumanChallenge(null);
    setHumanAnswer('');
    const response = await fetch('/api/contact.php?action=challenge', {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    const data = (await response.json().catch(() => null)) as
      | { success?: boolean; question?: string; token?: string; message?: string }
      | null;
    if (!response.ok || !data?.success || !data.question || !data.token) {
      throw new Error(data?.message || 'Unable to load the human verification check.');
    }
    setHumanChallenge({ question: data.question, token: data.token });
  }, []);

  useEffect(() => {
    void loadHumanChallenge().catch((challengeError) => {
      setError(challengeError instanceof Error ? challengeError.message : 'Unable to load human verification.');
    });
  }, [loadHumanChallenge]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!humanChallenge || humanAnswer.trim() === '') {
      setError('Please complete the human verification question.');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          ...formData,
          humanAnswer: humanAnswer.trim(),
          challengeToken: humanChallenge.token,
          website,
        }),
      });
      const data = (await res.json().catch(() => null)) as
        | { success?: boolean; message?: string }
        | null;

      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Unable to send message.');
      }

      setSubmitted(true);
      setHumanChallenge(null);
      setHumanAnswer('');
      setWebsite('');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to send message. Please try again or call us directly.';
      setError(message);
      void loadHumanChallenge().catch(() => undefined);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: 'Phone Support',
      details: ['928-565-6707', 'Monday–Friday, 9 AM – 5 PM (Arizona Time)'],
      action: 'Call Now',
      href: 'tel:928-565-6707'
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Support Chat',
      details: ['Same chat as the Airmax phone app', 'Enter your account phone to start'],
      action: 'Open Support Chat',
      href: '/chat'
    },
    {
      icon: ClockIcon,
      title: 'Support Hours',
      details: ['Phone: Mon–Fri 9 AM–5 PM (AZ)', 'Online Support Center: Available 24/7'],
      action: 'Visit Support Center',
      href: '/support'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-airmax-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              We're here to help you with all your internet service needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-airmax-blue rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <info.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{info.title}</h3>
                <div className="space-y-1 mb-4">
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="text-gray-600 text-sm">{detail}</p>
                  ))}
                </div>
                <a
                  href={info.href}
                  className="text-airmax-blue font-semibold hover:text-blue-700 transition-colors inline-flex items-center"
                >
                  {info.action}
                  <PaperAirplaneIcon className="h-4 w-4 ml-1 rotate-45" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave Separator 1 */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L48,58.7C96,53,192,43,288,48C384,53,480,75,576,80C672,85,768,75,864,64C960,53,1056,43,1152,48C1248,53,1344,75,1392,85.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#f9fafb"/>
          <path d="M0,96L48,90.7C96,85,192,75,288,74.7C384,75,480,85,576,85.3C672,85,768,75,864,64C960,53,1056,43,1152,48C1248,53,1344,75,1392,85.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#fbbf24" fillOpacity="0.5"/>
        </svg>
      </div>

      {/* Contact Form and Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              
              {submitted ? (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <PaperAirplaneIcon className="h-8 w-8 text-green-600 rotate-45" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Message Sent!</h3>
                  <p className="text-green-700 mb-4">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      void loadHumanChallenge().catch((challengeError) => {
                        setError(challengeError instanceof Error ? challengeError.message : 'Unable to load human verification.');
                      });
                    }}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8">
                  <div className="space-y-6">
                    <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={website}
                        onChange={(event) => setWebsite(event.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    {error && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                      </div>
                    )}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airmax-blue focus:border-transparent"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airmax-blue focus:border-transparent"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airmax-blue focus:border-transparent"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airmax-blue focus:border-transparent"
                        placeholder="Please provide details about your inquiry..."
                      />
                    </div>

                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                      <label htmlFor="humanAnswer" className="block text-sm font-semibold text-gray-800 mb-2">
                        Human verification *
                      </label>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-700 min-w-fit">
                          {humanChallenge ? `${humanChallenge.question} =` : 'Loading question…'}
                        </span>
                        <input
                          type="number"
                          id="humanAnswer"
                          name="humanAnswer"
                          value={humanAnswer}
                          onChange={(event) => setHumanAnswer(event.target.value)}
                          required
                          disabled={!humanChallenge}
                          inputMode="numeric"
                          className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airmax-blue focus:border-transparent disabled:bg-gray-100"
                          aria-describedby="human-check-help"
                        />
                      </div>
                      <p id="human-check-help" className="mt-2 text-xs text-gray-600">
                        Answer this quick question to show you are a person, not an automated bot.
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !humanChallenge}
                      className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>

            {/* Office Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-airmax-blue rounded-xl p-8 text-white mb-8">
                <h3 className="text-2xl font-bold mb-4">After-Hours Assistance</h3>
                <p className="text-blue-100 mb-6">
                  Live telephone assistance is available during regular business hours only.
                  For help anytime — including evenings and weekends — visit our 24/7 Self-Service Support Center
                  for troubleshooting, account access, payments, and support resources.
                </p>
                <a
                  href="/support"
                  className="bg-white text-airmax-blue px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center"
                >
                  Visit Support Center
                </a>
              </div>

              <h2 id="office-hours" className="text-3xl font-bold text-gray-900 mb-6">Support Hours</h2>
              
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-start mb-6">
                  <ClockIcon className="h-6 w-6 text-airmax-blue mr-3 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Phone Support</h3>
                    <p className="text-gray-600 mb-1">Monday–Friday: 9:00 AM – 5:00 PM (Arizona Time)</p>
                    <p className="text-gray-600 mb-4">Saturday–Sunday: Online Self-Service Support Only</p>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Online Support Center</h3>
                    <p className="text-gray-600">Available 24 hours a day, 7 days a week</p>
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    <strong>Note:</strong> Live telephone representatives are available during regular business hours only.
                    Customers can still get help anytime through our{' '}
                    <a href="/support" className="text-airmax-blue font-semibold hover:underline">
                      24/7 Self-Service Support Center
                    </a>
                    {' '}at airmaxcm.com/support — including troubleshooting guides, account access, billing, quick payments, and support requests.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wave Separator 2 */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#f9fafb"/>
          <path d="M0,96L48,90.7C96,85,192,75,288,74.7C384,75,480,85,576,85.3C672,85,768,75,864,64C960,53,1056,43,1152,48C1248,53,1344,75,1392,85.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#34d399" fillOpacity="0.5"/>
        </svg>
      </div>

      {/* Service Area Map */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Service Area
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're proud to serve communities across Arizona.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Currently Serving</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-gray-700">Golden Valley, AZ</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-gray-700">Kingman, AZ</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Coming Soon</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                    <span className="text-gray-700">Bullhead City, AZ</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                    <span className="text-gray-700">Lake Havasu City, AZ</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
              <p className="text-blue-800 text-center">
                <strong>Don't see your area listed?</strong> We're constantly expanding! 
                Contact us to inquire about service availability in your location.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
